import { Plus } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { pageHasFormActionBar } from '@/components/form-action-bar';

/**
 * "New lead", reachable from anywhere signed in.
 *
 * A lead arrives while the operator is mid-way through something else —
 * reading a contact, working the follow-up list, looking at the dashboard.
 * Making them navigate to Leads first is how one gets lost, and the whole
 * point of this CRM is that none do.
 *
 * Hidden on the lead form, where it would offer the page you are already on,
 * and on any page whose own actions are pinned to the bottom — this floats
 * 72px up, and the strip is about 68px tall above the phone tab bar, so the
 * two land on top of each other.
 *
 * AND HIDDEN ENTIRELY ON DESKTOP, where the same action now sits in the
 * header beside the bell. Fixed to the viewport means no amount of page
 * padding moves it off what is underneath: on the lead page it lay across the
 * "What happened" box and the submit button of the close form, at every
 * scroll position. A phone has no sidebar and still needs it; a desktop has
 * the nav rail and the header, so the floating copy was only ever in the way.
 */
export function NewLeadFab() {
  const { pathname } = useLocation();
  if (pageHasFormActionBar(pathname)) return null;

  return (
    <Link
      to="/leads/new"
      /*
       * z-40 sits under dialogs (50) and toasts (60): a modal must cover this,
       * and a toast must not be covered BY it. The toast container carries a
       * matching bottom padding so the two never share the same corner.
       */
      className={[
        // Above the bottom bar on a phone (56px + the notch). `md:hidden`
        // retires it at the width where the header carries the same action;
        // it also ends the fight with Netlify's admin HUD, which injects
        // itself into this corner on the deployed site and was reported as
        // "New lead button is hidden behind powered by netlify".
        'fixed right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-40 md:hidden',
        'flex items-center gap-2 rounded-full',
        'bg-brand py-3.5 pl-4 pr-5 text-[15px] font-bold text-brand-ink',
        'shadow-lg shadow-brand/30 transition-colors hover:bg-brand-hover',
        // Print is the one place a floating control is only ever in the way.
        'print:hidden',
      ].join(' ')}
    >
      <Plus className="size-5" aria-hidden="true" />
      {/*
        The label is hidden below `sm` but stays in the accessible name, so the
        button is a circle on a phone and still announces itself properly.
      */}
      <span className="sr-only sm:not-sr-only">New lead</span>
    </Link>
  );
}
